'use client';

import {signout} from '@/app/(auth)/signout/actions';
import Icon from '@/components/icon/Icon';
import NavigationItem from '@/components/navigationItem/NavigationItem';
import OnboardingStatus from '@/components/onboardingStatus/OnboardingStatus';
import ServiceModule from '@/components/serviceModule/ServiceModule';
import {NavigationMenuLink, navigationMenuTriggerStyle} from '@/components/ui/navigation-menu';
import {Separator} from '@/components/ui/separator';
import {useNavigationStateContext} from '@/context/NavigationProvider';
import type CustomerDetails from '@/types/CustomerDetails';
import type Navigation from '@/types/Navigation';
import type {NavigationGroup} from '@/types/Navigation';
import type NavigationItemType from '@/types/NavigationItem';
import type OnboardingStep from '@/types/OnboardingStep';
import {Loader2, LogOut} from 'lucide-react';
import {Fragment, useState} from 'react';

type NavigationContentProps = {
  customerDetails: CustomerDetails;
  navigationGroups?: NavigationGroup[];
  step?: OnboardingStep;
  serviceModule?: Navigation['serviceModule'];
  profileProgress?: {label: string; completed: boolean}[];
};

export default function NavigationContent({
  customerDetails,
  navigationGroups,
  profileProgress,
  serviceModule,
  step,
}: NavigationContentProps) {
  const {isNavOpen, toggleNav} = useNavigationStateContext();
  const firstIndex = 0;
  const lastIndex = (navigationGroups && navigationGroups?.length - 1) || 0;

  function handleOnClick() {
    if (isNavOpen) {
      toggleNav();
    }
  }

  return (
    <>
      {customerDetails.firstName && <h2 className="text-2xl">Hi, {customerDetails.firstName}</h2>}
      {customerDetails.externalId && (
        <p>
          Member number: <strong>{customerDetails.externalId}</strong>
        </p>
      )}
      {!!navigationGroups &&
        navigationGroups.map((navigationGroup, index) => (
          <Fragment key={navigationGroup.key}>
            <ul
              className={`flex w-full flex-col gap-4 ${index === firstIndex ? 'mt-6' : undefined} ${index === lastIndex ? 'mb-6' : undefined}`}
            >
              {navigationGroup.navigationItems.map((navigationItem) => (
                <li className="flex" key={navigationItem.key}>
                  <NavigationItemSwitch data={navigationItem} onClick={handleOnClick} />
                </li>
              ))}
            </ul>
            {index !== lastIndex && <Separator className="mb-6 mt-4" />}
          </Fragment>
        ))}
      {!!step && (
        <OnboardingStatus onClick={handleOnClick} profileProgress={profileProgress} step={step} />
      )}
      {serviceModule && <ServiceModule data={serviceModule} />}
    </>
  );
}

type NavigationItemSwitchProps = {
  data: NavigationItemType;
  onClick: () => void;
};

function NavigationItemSwitch({data, onClick}: NavigationItemSwitchProps) {
  const [loading, setLoading] = useState(false);

  const handleSignOut = async () => {
    setLoading(true);
    await signout({});
    setLoading(false);
  };

  switch (data.linkType) {
    case 'external':
      return (
        <NavigationItem href={data.url ?? ''} onClick={onClick} variant="sidebar">
          <Icon name={data.icon} />
          {data.label}
        </NavigationItem>
      );
    case 'page':
      if (data.pageSlug === 'sign-out') {
        return (
          <NavigationMenuLink
            className={navigationMenuTriggerStyle({
              className: 'flex-1 cursor-pointer justify-start gap-4',
            })}
            active={false}
            onClick={() => void handleSignOut()}
          >
            {loading ? <Loader2 className="animate-spin" /> : <LogOut />}
            Sign Out
          </NavigationMenuLink>
        );
      }

      return (
        <NavigationItem href={`/${data.pageSlug}`} onClick={onClick} variant="sidebar">
          <Icon name={data.icon} />
          {data.label}
        </NavigationItem>
      );
    default:
      return null;
  }
}
